import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { ControlledCheckbox, ControlledRadioGroup, ControlledTextField } from '../../../../shared/components';

/** Whether the per-visit fees cover travel, lodging and food — each prints its own note. */
const EXPENSES_OPTIONS = [
  { label: 'Including all expenses', value: 'INCLUDING' },
  { label: 'Excluding all expenses', value: 'EXCLUDING' },
];

const fieldWrapperSx = {
  '& label': { color: 'grey.500' },
  '& > div': {
    gridTemplateColumns: '10rem auto',
    columnGap: 8,
  },
};

/**
 * `inScope` drives the quotation PDF's site-visit note: on, it prints the two per-visit
 * rates; off, it prints "Site Visit- Not in Scope". The rates stay on screen but disabled
 * when it is off, so turning it back on does not mean re-typing them.
 */
export default function SiteVisitSection({
  engineerCanHaveFreeVisits,
  directorCanHaveFreeVisits,
  inScope = true,
}) {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: '10rem auto', columnGap: 2, rowGap: 1 }}>
      <Typography variant='body1' fontWeight={500} sx={{ whiteSpace: 'nowrap' }}>
        2. Site Visit Fees
      </Typography>
      <div className='column gap-1'>
        <ControlledCheckbox
          name='siteVisit.inScope'
          label='Site visits are in scope (quotation prints per-visit charges)'
        />
        {inScope && (
          <ControlledRadioGroup
            name='siteVisit.expenses'
            label='Visit fees are'
            options={EXPENSES_OPTIONS}
            wrapperOrientation='row'
            rules={{ required: 'Choose whether the visit fees include all expenses' }}
            wrapperSx={{ ml: 4 }}
          />
        )}
        <SiteVisitorChargeRow
          visitorType='engineer'
          label='Site Engineer'
          freeVisitsEnabled={engineerCanHaveFreeVisits && inScope}
          disabled={!inScope}
        />
        <SiteVisitorChargeRow
          visitorType='director'
          label='Director'
          freeVisitsEnabled={directorCanHaveFreeVisits && inScope}
          disabled={!inScope}
        />
      </div>
    </Box>
  );
}

function SiteVisitorChargeRow({ visitorType, label, freeVisitsEnabled, disabled = false }) {
  return (
    <div className='row gap-3'>
      <ControlledTextField
        name={`siteVisit.${visitorType}.charge`}
        id={`${visitorType}-visit-charge`}
        rules={{
          validate: (v) => {
            if (!v) return true;
            if (Number(v) <= 0) return 'Amount must be greater than zero';
            return true;
          },
        }}
        disabled={disabled}
        label={label}
        placeholder='Enter amount'
        inputMode='numeric'
        showErrorInline
        reserveErrorSpace
        prefix='₹'
        sx={{ maxWidth: 260, width: '100%' }}
        wrapperSx={fieldWrapperSx}
        orientation='row'
      />
      <ControlledTextField
        name={`siteVisit.${visitorType}.freeVisits`}
        id={`${visitorType}-visit-freeCount`}
        rules={{
          validate: (v, values) => {
            const amount = values.siteVisit?.[visitorType]?.charge;

            if (!amount) return true; // disabled anyway
            if (v === '' || v === null) return true;
            if (Number(v) < 0) return 'Free visits cannot be negative';

            return true;
          },
        }}
        disabled={!freeVisitsEnabled}
        label='Max no. of free site visits'
        placeholder='Enter count'
        inputMode='numeric'
        showErrorInline
        reserveErrorSpace
        sx={{ maxWidth: 260, width: '100%' }}
        wrapperSx={{ ...fieldWrapperSx, ml: 4 }}
        orientation='row'
      />
    </div>
  );
}
